import { AlertCircle, Terminal, HelpCircle } from "lucide-react";
import type { MapStyleCreateSheetBodyProps } from "../../contracts/map-styles";
import { cn } from "../../lib/utils";
import { Input } from "../../internal/primitives/input";
import { Label } from "../../internal/primitives/label";
import { Switch } from "../../internal/primitives/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../internal/primitives/select";

export function MapStyleCreateSheetBody({
  title,
  description,
  name,
  details,
  shareScope,
  shareScopeOptions,
  starterValue,
  starterOptions,
  advancedMode = false,
  jsonValue = "",
  errorMessage,
  submitting,
  onNameChange,
  onDetailsChange,
  onShareScopeChange,
  onStarterChange,
  onAdvancedModeChange,
  onJsonValueChange,
  onCancel,
  onSubmit,
  className,
}: MapStyleCreateSheetBodyProps) {
  return (
    <div className={cn("flex flex-col gap-6 p-1", className)}>
      <div className="space-y-1">
        <h2 className="text-lg font-black tracking-tight text-foreground">
          {title}
        </h2>
        {description && (
          <p className="text-xs text-muted-foreground/80">
            {description}
          </p>
        )}
      </div>

      {errorMessage && (
        <div className="flex items-center gap-3 rounded-lg border border-destructive/20 bg-destructive/5 p-3 text-destructive">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <p className="text-xs font-bold leading-tight">{errorMessage}</p>
        </div>
      )}

      <div className="space-y-5">
        <div className="space-y-2">
          <Label htmlFor="style-name" className="text-[10px] font-black uppercase tracking-wider">
            Style Name
          </Label>
          <Input
            id="style-name"
            value={name}
            onChange={(e) => onNameChange(e.target.value)}
            placeholder="e.g. Night Operations v2"
            className="text-xs"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="style-details" className="text-[10px] font-black uppercase tracking-wider">
            Description
          </Label>
          <Input
            id="style-details"
            value={details}
            onChange={(e) => onDetailsChange(e.target.value)}
            placeholder="What is this style for?"
            className="text-xs"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-[10px] font-black uppercase tracking-wider">
              Share Scope
            </Label>
            <Select value={shareScope} onValueChange={onShareScopeChange as any}>
              <SelectTrigger className="text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {shareScopeOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value} disabled={opt.disabled} className="text-xs">
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-[10px] font-black uppercase tracking-wider">
              Starter Template
            </Label>
            <Select value={starterValue} onValueChange={onStarterChange}>
              <SelectTrigger className="text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {starterOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value} className="text-xs">
                    <div className="flex flex-col gap-0.5">
                      <span className="font-bold">{opt.label}</span>
                      {opt.description && (
                        <span className="text-[9px] text-muted-foreground/70">{opt.description}</span>
                      )}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {onAdvancedModeChange && (
          <div className="flex items-center justify-between rounded-lg border border-border/40 bg-muted/20 p-3">
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-background ring-1 ring-border/60">
                <Terminal className="h-4 w-4 text-muted-foreground" />
              </div>
              <div className="space-y-0.5">
                <h4 className="text-[11px] font-black uppercase tracking-tight text-foreground">Advanced JSON Mode</h4>
                <p className="text-[10px] text-muted-foreground/70 italic">Skip starters and provide raw MapLibre JSON</p>
              </div>
            </div>
            <Switch
              checked={advancedMode}
              onCheckedChange={onAdvancedModeChange}
            />
          </div>
        )}

        {advancedMode && onJsonValueChange && (
          <div className="space-y-2 animate-in fade-in slide-in-from-top-2 duration-200">
            <div className="flex items-center justify-between">
              <Label className="text-[10px] font-black uppercase tracking-wider">
                Raw Style JSON
              </Label>
              <div className="flex items-center gap-1.5 text-[9px] font-medium text-muted-foreground/60">
                <HelpCircle className="h-3 w-3" />
                Must follow Mapbox/MapLibre spec
              </div>
            </div>
            <textarea
              value={jsonValue}
              onChange={(e) => onJsonValueChange(e.target.value)}
              spellCheck={false}
              className="min-h-[200px] w-full rounded-md border border-border/60 bg-muted/30 p-3 font-mono text-[11px] text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              placeholder='{ "version": 8, "name": "...", "sources": {}, "layers": [] }'
            />
          </div>
        )}
      </div>

      <div className="mt-4 flex items-center justify-end gap-3 border-t border-border/40 pt-6">
        {onCancel && (
          <button
            onClick={onCancel}
            disabled={submitting}
            className="h-9 px-4 text-xs font-bold text-muted-foreground hover:text-foreground disabled:opacity-50"
          >
            Cancel
          </button>
        )}
        {onSubmit && (
          <button
            onClick={onSubmit}
            disabled={submitting || !name}
            className="inline-flex h-9 items-center justify-center rounded-md bg-primary px-6 py-2 text-xs font-black text-primary-foreground shadow-sm transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50"
          >
            {submitting ? (
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary-foreground border-t-transparent" />
            ) : (
              "Create Style"
            )}
          </button>
        )}
      </div>
    </div>
  );
}
